// ConsortiumDialog.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Popover,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
} from '@mui/material';
import {
  Science as ResearcherIcon,
  AccountTree as WPLeaderIcon,
  Check as CheckIcon,
  AddCircleOutline as NewIcon,
  Group as GroupIcon,
} from '@mui/icons-material';

import { axiosService } from 'shared/helpers/axios';
import { useUser } from "shared/helpers/axios";
import { fetchUsers as fetchUsersAPI } from '../../../shared/utils/userApi';
import { useTheme } from '@mui/material/styles';


/**
 * ConsortiumDialog
 * - Creates a consortium for a given call
 * - Lets the creator (you) invite up to 10 users
 * - Selected users are managed as user objects (not {user, role}), with a roles map
 */
const ConsortiumDialog = ({
  open,
  onClose,
  call,
  potentialMembers = [],
  loading: externalLoading = false,
  maxInvites = 10,
}) => {
  // Form
  const [consortiumName, setConsortiumName] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [requestedAmount, setRequestedAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [proposalReference, setProposalReference] = useState('');

  // Users
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // array of user objects
  const [rolesByUserId, setRolesByUserId] = useState({}); // { <userId>: 'role' }
  const [searchQuery, setSearchQuery] = useState('');
  const [emailInvitees, setEmailInvitees] = useState([]);
  const [emailInput, setEmailInput] = useState('');

  // Role popover
  const [rolePopoverAnchor, setRolePopoverAnchor] = useState(null);
  const [pendingRoleUserId, setPendingRoleUserId] = useState(null);

  // Status
  const [loading, setLoading] = useState(false); // users search/loading
  const [submitting, setSubmitting] = useState(false); // create + invites
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [userMemberships, setUserMemberships] = useState([]);
  const [, setLoadingMemberships] = useState(false);

  // Existing consortium mode
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [selectedExistingConsortium, setSelectedExistingConsortium] = useState(null);
  const [existingConsortia, setExistingConsortia] = useState([]);
  const [loadingConsortia, setLoadingConsortia] = useState(false);
  const [consortiaQuery, setConsortiaQuery] = useState('');

  const theme = useTheme();
  // Debounce search
  const debounceRef = useRef();

  const { user: currentUser }  = useUser();

  const canSubmit = (
    mode === 'existing'
      ? selectedExistingConsortium !== null
      : consortiumName.trim().length > 0
  ) && !submitting;

  // ---- Effects ----

  // Reset when dialog opens/closes
  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(false);
      setInviteMessage('');
      // Seed roles from potentialMembers suggestions if they exist
      const initialRoles = {};
      potentialMembers.forEach((m) => {
        const id = String(m?.id ?? m?.public_id ?? '');
        if (id) initialRoles[id] = m.suggestedRole || 'researcher';
      });
      setRolesByUserId(initialRoles);
      // Fetch user's memberships
      fetchUserMemberships();
    } else {
      setConsortiumName('');
      setSelectedUsers([]);
      setUsers([]);
      setSearchQuery('');
      setRolesByUserId({});
      setUserMemberships([]);
      setRequestedAmount('');
      setCurrency('EUR');
      setProposalReference('');
      setMode('new');
      setSelectedExistingConsortium(null);
      setExistingConsortia([]);
      setConsortiaQuery('');
      setEmailInvitees([]);
      setEmailInput('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, potentialMembers]);

  // Fetch user's organization memberships
  const fetchUserMemberships = async () => {
    try {
      setLoadingMemberships(true);
      const res = await axiosService.get('/consortium/organization-memberships/?user=' + encodeURIComponent(currentUser?.id)  );
      setUserMemberships(res.data?.results ?? res.data ?? []);
      // console.log('User memberships fetched:', res.data);

    } catch (e) {
      console.error('Error fetching memberships:', e);
    } finally {
      setLoadingMemberships(false);
    }
  };

  // Fetch users (simple search)
  const fetchUsers = async (q = '') => {
    try {
      setLoading(true);

      let results = [];

      if (!q || q.trim().length < 2) {
        // For empty or too short query, fetch initial set
        const res = await axiosService.get('/users/?page_size=50');
        results = res.data?.results ?? res.data ?? [];
      } else {
        // Use shared fetchUsers utility for search
        results = await fetchUsersAPI(q);
      }
      results = results.filter((u) => String(u?.id ?? u?.public_id ?? '') !== String(currentUser?.id ?? ''));
      setUsers(results);
    } catch (e) {
      console.error('Error fetching users:', e);
      // non-blocking
    } finally {
      setLoading(false);
    }
  };

  // Initial load + on search query change (debounced)
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchUsers(searchQuery), 300);
    return () => clearTimeout(debounceRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, searchQuery]);

  // Load consortia when mode switches to 'existing'
  useEffect(() => {
    if (!open || mode !== 'existing') return;
    setLoadingConsortia(true);
    axiosService.get('/consortium/consortiums/', { params: { page_size: 30 } })
      .then(res => setExistingConsortia(res.data?.results || res.data || []))
      .catch(() => setExistingConsortia([]))
      .finally(() => setLoadingConsortia(false));
  }, [open, mode]);

  // Debounced consortia search
  useEffect(() => {
    if (!open || mode !== 'existing') return;
    if (consortiaQuery.length < 2) {
      if (consortiaQuery === '') {
        setLoadingConsortia(true);
        axiosService.get('/consortium/consortiums/', { params: { page_size: 30 } })
          .then(res => setExistingConsortia(res.data?.results || res.data || []))
          .catch(() => setExistingConsortia([]))
          .finally(() => setLoadingConsortia(false));
      }
      return;
    }
    const timer = setTimeout(() => {
      setLoadingConsortia(true);
      axiosService.get('/consortium/consortiums/', { params: { search: consortiaQuery, page_size: 20 } })
        .then(res => setExistingConsortia(res.data?.results || res.data || []))
        .catch(() => setExistingConsortia([]))
        .finally(() => setLoadingConsortia(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [consortiaQuery, open, mode]);

  // ---- Helpers ----
  const isOptionEqualToValue = (opt, val) =>
    String(opt?.id ?? opt?.public_id ?? '') ===
    String(val?.id ?? val?.public_id ?? '');

  const handleRoleChange = (userId, role) => {
    setRolesByUserId((prev) => ({ ...prev, [String(userId)]: role }));
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  // ---- Submit flow ----
  const handleSubmit = async () => {
    if (mode === 'new' && !consortiumName.trim()) {
      setError('Please enter a consortium name.');
      return;
    }
    if (mode === 'existing' && !selectedExistingConsortium) {
      setError('Please select an existing consortium.');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);

      if (!currentUser?.id) {
        throw new Error('User info unavailable. Please re-login.');
      }

      let consortiumId;

      if (mode === 'existing') {
        // Add a new funding collaboration to the existing consortium
        consortiumId = selectedExistingConsortium.id;
        if (!call?.id) throw new Error('Call information is missing.');
        await axiosService.post('/consortium/collaborations/', {
          consortium: consortiumId,
          title: selectedExistingConsortium.name,
          category: 'funding',
          abstract: `Funding application for ${call?.title || 'collaborative research'}`,
          status: 'planning',
          call: call.id,
          keywords: [],
        });
        // Backend signal notifies existing consortium members automatically
      } else {
        // Check if user has at least one verified organization membership
        const hasVerifiedMembership = userMemberships.some(
          (m) => m.is_verified === true
        );
        if (!hasVerifiedMembership) {
          throw new Error(
            'You must have at least one verified organization membership before creating a consortium. Please add and verify your organization in your profile.'
          );
        }
        if (!call?.id) throw new Error('Call information is missing.');

        const collaborationData = {
          consortium_name: consortiumName.trim(),
          title: consortiumName.trim(),
          category: 'funding',
          abstract: `Consortium for ${call?.title || 'collaborative research'}`,
          status: 'planning',
          call: call.id,
          keywords: [],
        };

        const typeSpecificData = {};
        if (requestedAmount) typeSpecificData.requested_amount = requestedAmount;
        if (currency) typeSpecificData.currency = currency;
        if (proposalReference) typeSpecificData.proposal_reference = proposalReference;

        const createRes = await axiosService.post('/consortium/collaborations/', {
          collaboration: collaborationData,
          typeSpecific: typeSpecificData,
          collaborationType: 'funding',
          duration_months: 24,
          status: 'forming',
        });

        consortiumId = createRes.data?.consortium_id;
        if (!consortiumId) throw new Error('Consortium was not created properly.');
      }

      // In 'existing' mode, existing members are already notified via consortium_update.
      // Only send invitations when creating a brand-new consortium (new mode).
      if (mode !== 'existing') {
        const toInvite = selectedUsers.filter(
          (u) => String(u?.id ?? u?.public_id ?? '') !== String(currentUser?.id ?? '')
        );
        if (toInvite.length > 0 || emailInvitees.length > 0) {
          const results = await Promise.allSettled([
            ...toInvite.map((u) =>
              axiosService.post('/interact/consortium-invitations/', {
                consortium: consortiumId,
                recipient: u.id ?? u.public_id,
                message: inviteMessage || undefined,
              })
            ),
            ...emailInvitees.map((email) =>
              axiosService.post('/interact/consortium-invitations/', {
                consortium: consortiumId,
                to_email: email,
              })
            ),
          ]);
          const failed = results.filter((r) => r.status !== 'fulfilled').length;
          if (failed) {
            setError(
              `Consortium created, but ${failed} invitation${failed > 1 ? 's' : ''} failed to send.`
            );
          }
        }
      }

      setSuccess(true);
      setTimeout(() => handleClose(), 1500);
    } catch (err) {
      const api = err?.response?.data;
      const msg =
        (typeof api === 'string' && api) ||
        (api &&
          Object.entries(api)
            .map(([k, v]) =>
              Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${String(v)}`
            )
            .join('; ')) ||
        err?.message ||
        'Failed. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- UI ----
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'existing' ? 'Add Funding Call to Existing Consortium' : 'Build Consortium for Funding Call'}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Consortium created successfully! Invitations have been sent (if
              selected).
            </Alert>
          )}

          {/* Call header */}
          <Typography variant="h6" gutterBottom>
            {call?.title}
          </Typography>

          {/* Mode toggle */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Card
              sx={{
                cursor: 'pointer',
                border: 2,
                borderColor: mode === 'new' ? 'primary.main' : 'divider',
                bgcolor: mode === 'new' ? 'primary.50' : 'background.paper',
                flex: 1,
                transition: 'all 0.2s',
                '&:hover': { borderColor: 'primary.main' },
              }}
              onClick={() => { setMode('new'); setSelectedExistingConsortium(null); }}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NewIcon sx={{ color: mode === 'new' ? 'primary.main' : 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>New Consortium</Typography>
                    <Typography variant="caption" color="text.secondary">Create a brand new consortium</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card
              sx={{
                cursor: 'pointer',
                border: 2,
                borderColor: mode === 'existing' ? 'secondary.main' : 'divider',
                bgcolor: mode === 'existing' ? 'secondary.50' : 'background.paper',
                flex: 1,
                transition: 'all 0.2s',
                '&:hover': { borderColor: 'secondary.main' },
              }}
              onClick={() => { setMode('existing'); setConsortiumName(''); }}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon sx={{ color: mode === 'existing' ? 'secondary.main' : 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>I Already Have a Consortium</Typography>
                    <Typography variant="caption" color="text.secondary">Add this call to an existing consortium</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Consortium name (new mode) or autocomplete (existing mode) */}
          {mode === 'new' ? (
            <TextField
              label="Consortium Name"
              fullWidth
              value={consortiumName}
              onChange={(e) => setConsortiumName(e.target.value)}
              helperText="Choose a descriptive name."
            />
          ) : (
            <Box>
              <Autocomplete
                value={selectedExistingConsortium}
                onChange={(_, val) => setSelectedExistingConsortium(val)}
                onInputChange={(_, val, reason) => {
                  if (reason === 'input') setConsortiaQuery(val);
                  else if (reason === 'clear') setConsortiaQuery('');
                }}
                options={existingConsortia}
                getOptionLabel={(o) => o?.name || ''}
                isOptionEqualToValue={(o, v) => o?.id === v?.id}
                filterOptions={(x) => x}
                loading={loadingConsortia}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Your Consortium"
                    placeholder="Search by name…"
                    helperText="Select an existing consortium to apply for this call"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingConsortia && <CircularProgress size={18} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Typography variant="body2">{option.name}</Typography>
                  </Box>
                )}
              />
              {selectedExistingConsortium && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Existing members of <strong>{selectedExistingConsortium.name}</strong> will be notified about this new project.
                </Alert>
              )}
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Recommended members */}
          {/* {Array.isArray(potentialMembers) && potentialMembers.length > 0 && (
            <Paper
              variant="outlined"
              sx={{ mb: 3, p: 2, bgcolor: 'rgba(76,175,80,0.04)' }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: 'success.main', fontWeight: 600 }}
              >
                🎯 Recommended Partners
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Based on call requirements and expertise:
              </Typography>

         

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
  {potentialMembers.slice(0, 10).map((m) => {
    const id = String(m?.id ?? m?.public_id ?? '');
    const added = selectedIds.has(id);
    
    return (
      <Chip
        key={id}
        avatar={
          <Avatar src={m.avatar} sx={{ width: 24, height: 24 }}>
            {(m.name || m.username || 'U').charAt(0).toUpperCase()}
          </Avatar>
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={500}>
              {m.name || m.username || 'Unknown'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({m.suggestedRole || 'researcher'})
            </Typography>
          </Box>
        }
        onClick={() => toggleRecommended(m)}
        variant={added ? "filled" : "outlined"}
        color={added ? "success" : "default"}
        sx={{
          cursor: 'pointer',
          '& .MuiChip-avatar': {
            width: 24,
            height: 24,
            ml: '4px',
          },
          bgcolor: added ? 'success.main' : 'transparent',
          borderColor: added ? 'success.main' : 'rgba(118, 75, 162, 0.3)',
          '&:hover': {
            bgcolor: added ? 'success.dark' : 'rgba(118, 75, 162, 0.04)',
            borderColor: 'rgba(118, 75, 162, 0.5)',
          },
        }}
      />
    );
  })}
</Stack>
  


            </Paper>
          )}
 */}
          {/* <Divider sx={{ my: 1 }} /> */}

          {/* Members search + select */}
          <Typography variant="subtitle1" gutterBottom>
            Invite Members (up to {maxInvites})
          </Typography>

          <Autocomplete
            multiple
      
            value={selectedUsers}
            onChange={(_e, newValue, reason, details) => {
              // Enforce max
              const next =
                newValue.length > maxInvites
                  ? newValue.slice(0, maxInvites)
                  : newValue;

              // Initialize roles for new users if not set
              const updatedRoles = { ...rolesByUserId };
              next.forEach((u) => {
                const id = String(u?.id ?? u?.public_id ?? '');
                if (id && !updatedRoles[id]) {
                  updatedRoles[id] = 'researcher';
                }
              });
              setRolesByUserId(updatedRoles);
              setSelectedUsers(next);

              // Open role picker when a new user is added
              if (reason === 'selectOption' && details?.option) {
                const newUserId = String(details.option?.id ?? details.option?.public_id ?? '');
                if (newUserId) {
                  setPendingRoleUserId(newUserId);
                  // Anchor to the autocomplete input area
                  setTimeout(() => {
                    const chipEls = document.querySelectorAll(`[data-role-chip-id="${newUserId}"]`);
                    if (chipEls.length > 0) {
                      setRolePopoverAnchor(chipEls[chipEls.length - 1]);
                    }
                  }, 50);
                }
              }
            }}
            options={users}
            onInputChange={(_e, v) => setSearchQuery(v)}
            loading={loading || externalLoading}
            isOptionEqualToValue={isOptionEqualToValue}

            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar 
                    src={option.avatar} 
                    sx={{ 
                      width: 24, 
                      height: 24,
                      bgcolor: theme.palette.primary.avatar2,//'rgba(118, 75, 162, 0.85)',
                      fontSize: '0.75rem',
                      color: '#fff',
                      fontWeight: 600,
                      
                    }}
                  >
                    {(option?.name || option?.username || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="body2">
                    {option?.name || option?.username || 'Unknown'}
                    {option?.email && (
                      <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
                        ({option.email})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </li>
            )}

            filterOptions={(options) => options} // Disable client-side filtering for server-side search

            renderTags={(value, getTagProps) =>
              value.map((u, idx) => {
                const uid = String(u?.id ?? u?.public_id ?? '');
                const role = rolesByUserId[uid] || 'researcher';
                const roleLabel = { wp_leader: 'WP Leader', researcher: 'Researcher', coordinator: 'Coordinator' }[role] || role;
                const tagProps = getTagProps({ index: idx });

                return (
                  <Chip
                    key={String(u?.id ?? u?.public_id ?? idx)}
                    data-role-chip-id={uid}
                    variant="outlined"
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 0.25 }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.3, fontWeight: 500 }}>
                          {u?.name || u?.username || 'Unknown'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            lineHeight: 1.2,
                            color: role === 'wp_leader' ? 'primary.main' : 'text.secondary',
                            fontWeight: role === 'wp_leader' ? 600 : 400,
                            fontSize: '0.9rem',
                          }}
                        >
                          {roleLabel}
                        </Typography>
                      </Box>
                    }
                    avatar={
                      u?.avatar ? (
                        <Avatar src={u.avatar} />
                      ) : (
                        <Avatar sx={{ 
                       
                          bgcolor: theme.palette.primary.avatar2,//'rgba(118, 75, 162, 0.85)',
                          fontSize: '1rem',
                          color: '#fff !important',
                          fontWeight: 600,
          
                         }}>
                          {(u?.name || u?.username || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                      )
                    }
                    {...tagProps}
                    onClick={(e) => {
                      // Open role picker on chip click
                      e.stopPropagation();
                      setPendingRoleUserId(uid);
                      setRolePopoverAnchor(e.currentTarget);
                    }}
                    sx={{
                      height: 'auto',
                      '& .MuiChip-label': { py: 0.5 },
                      '& .MuiChip-avatar': {
                        width: 28,
                        height: 28,
                      },
                      cursor: 'pointer',
                      borderColor: role === 'wp_leader' ? 'primary.main' : undefined,
                    }}
                  />
                );
              })
            }

            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Team Members"
                placeholder="Search by name, email or username…"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 2 }}
          />

          {/* Email invitees */}
          <Divider sx={{ my: 1 }}>
            <Typography variant="caption" color="text.secondary">or invite by email</Typography>
          </Divider>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              size="small"
              type="email"
              label="Invite by email address"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const addr = emailInput.trim().toLowerCase();
                  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr) && !emailInvitees.includes(addr)) {
                    setEmailInvitees((prev) => [...prev, addr]);
                    setEmailInput('');
                  }
                }
              }}
              placeholder="colleague@university.edu"
              fullWidth
            />
            <Button
              variant="outlined"
              size="small"
              sx={{ whiteSpace: 'nowrap' }}
              onClick={() => {
                const addr = emailInput.trim().toLowerCase();
                if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr) && !emailInvitees.includes(addr)) {
                  setEmailInvitees((prev) => [...prev, addr]);
                  setEmailInput('');
                }
              }}
            >
              Add
            </Button>
          </Box>
          {emailInvitees.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {emailInvitees.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  size="small"
                  onDelete={() => setEmailInvitees((prev) => prev.filter((e) => e !== email))}
                />
              ))}
            </Box>
          )}

          {/* Role selection popover */}
          <Popover
            open={Boolean(rolePopoverAnchor) && Boolean(pendingRoleUserId)}
            anchorEl={rolePopoverAnchor}
            onClose={() => {
              setRolePopoverAnchor(null);
              setPendingRoleUserId(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: 0.5, minWidth: 180, borderRadius: 2, boxShadow: 3 } } }}
          >
            <Box sx={{ py: 0.5 }}>
              <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                Assign Role
              </Typography>
              {[
                { value: 'researcher', label: 'Researcher', icon: <ResearcherIcon fontSize="small" /> },
                { value: 'wp_leader', label: 'WP Leader', icon: <WPLeaderIcon fontSize="small" /> },
              ].map((r) => {
                const isSelected = rolesByUserId[pendingRoleUserId] === r.value;
                return (
                  <ListItemButton
                    key={r.value}
                    selected={isSelected}
                    onClick={() => {
                      handleRoleChange(pendingRoleUserId, r.value);
                      setRolePopoverAnchor(null);
                      setPendingRoleUserId(null);
                    }}
                    sx={{ py: 0.75, px: 2 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>{r.icon}</ListItemIcon>
                    <ListItemText
                      primary={r.label}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                    {isSelected && <CheckIcon fontSize="small" color="primary" />}
                  </ListItemButton>
                );
              })}
            </Box>
          </Popover>



          {/* Roles are now assigned inline via chip popover — click a chip to change */}
          {selectedUsers.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              💡 Click a member chip to change their role
            </Typography>
          )}

          {/* Funding-specific fields */}
          {/* <Divider sx={{ my: 1 }} /> */}
          {/* <Typography variant="subtitle1" gutterBottom>
            Funding Application Details (Optional)
          </Typography>
           */}
          {/* <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Requested Amount"
              type="number"
              value={requestedAmount}
              onChange={(e) => setRequestedAmount(e.target.value)}
              sx={{ flex: 1 }}
              placeholder="0.00"
              InputProps={{
                startAdornment: currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency === 'GBP' ? '£' : currency,
              }}
              helperText="Amount you plan to request from this call"
            />
            
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Currency</InputLabel>
              <Select
                label="Currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
                <MenuItem value="CHF">CHF</MenuItem>
              </Select>
            </FormControl>
          </Stack> */}
          
          {/* <TextField
            label="Proposal Reference (Optional)"
            fullWidth
            value={proposalReference}
            onChange={(e) => setProposalReference(e.target.value)}
            placeholder="Your internal reference or proposal number"
            sx={{ mb: 1 }}
            helperText="Internal tracking number for your application"
          /> */}

          <Divider sx={{ my: 1 }} />

          {/* Optional message */}
          <TextField
            label="Invitation Message (Optional)"
            multiline
            rows={4}
            fullWidth
            value={inviteMessage}
            onChange={(e) => setInviteMessage(e.target.value)}
            placeholder="Add a personal note…"
          />

          {/* Call info (optional) */}
          {call?.funding_agency && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Call Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Agency:</strong> {call.funding_agency.name}
              </Typography>
              {call.funding_program && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Program:</strong> {call.funding_program.name}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                <strong>Deadline:</strong>{' '}
                {call.stage1_deadline
                  ? new Date(call.stage1_deadline).toLocaleDateString()
                  : 'TBD'}
              </Typography>
              {call.budget && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Budget:</strong> {call.budget}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Creating…
            </>
          ) : (
            'Create Consortium'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsortiumDialog;
